package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TransformOrigin
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.transformOrigin
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlin.random.Random
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.events.SyntheticKeyboardEvent
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.MouseEvent
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * One line of a menu: a command, a rule between two groups of them, or the name of a group.
 *
 * Sealed rather than three unrelated lists, because the order is the whole point — a separator
 * belongs *between* two particular items, and a parallel `separatorsAfter: Set<Int>` is a second
 * thing to keep in step with the first every time an item is added or a permission removes one.
 */
sealed interface LMenuEntry

/**
 * A command.
 *
 * [shortcut] is written the way a person writes one — `"⌘K"`, `"Ctrl K"`, `"⌘⇧↓"` — and split into
 * keys by [chordKeys]; pass what [commandChord] produces and the Mac and non-Mac spellings both
 * arrive correct. [description] is the second line under the label, for a command whose name cannot
 * carry its consequence ("Clear translation" — "Removes the draft; the approved value stays").
 *
 * [checked] is the difference between a command and a setting. `null` — the default, and what every
 * existing call site gets — is a command: it is announced as `menuitem`, and the row draws no
 * indicator column at all. Non-null makes the row a `menuitemcheckbox` with `aria-checked`, which is
 * what tells a listener that pressing it toggles something rather than doing something.
 */
class LMenuItem(
    val label: String,
    val disabled: Boolean = false,
    val danger: Boolean = false,
    val icon: (@Composable () -> Unit)? = null,
    val shortcut: String? = null,
    val description: String? = null,
    val checked: Boolean? = null,
) : LMenuEntry

/** A hairline between two runs of commands. Drawn, never announced, never focusable. */
data object LMenuSeparator : LMenuEntry

/**
 * The name of the run of items that follows it.
 *
 * `role="presentation"`, so the panel stays a flat list of commands to a screen reader rather than
 * gaining nested `group`s that have to be entered and left. The title is a sighted reader's landmark
 * for a long menu; a listener gets the same structure from the items' own labels, which is why a
 * group title is never the only place a distinction is stated.
 */
class LMenuGroup(val title: String) : LMenuEntry

enum class LMenuFocusPolicy {
    OWNED,
    DELEGATED,
}

/**
 * Which corner of the trigger the panel hangs from.
 *
 * Named logically — `START`/`END`, not left/right — because the whole point of the `--l-inline-*`
 * variables below is that a menu in Arabic grows from the corner its trigger is actually in.
 *
 * [BOTTOM_END] is the default and what every menu in the product drew before this existed: a panel
 * under the control, aligned to its trailing edge. The other three exist for the two shapes that
 * cannot use it — a trigger at the inline start of a wide row, whose panel would otherwise hang off
 * to the left of everything, and a trigger near the bottom of the viewport, whose panel has to open
 * upwards or be scrolled to.
 */
enum class LMenuPlacement(internal val attributeValue: String) {
    BOTTOM_START("bottom-start"),
    BOTTOM_END("bottom-end"),
    TOP_START("top-start"),
    TOP_END("top-end"),
}

val LMenuRootStyle = CssStyle.base {
    Modifier.position(Position.Relative)
}

val LMenuScrimStyle = CssStyle.base {
    Modifier
        .position(Position.Fixed)
        .top(0.px)
        .right(0.px)
        .bottom(0.px)
        .left(0.px)
        .zIndex(899)
}

/**
 * The panel grows from the corner of the control that opened it.
 *
 * It used to have no entrance at all — absent on one frame, complete on the next — which is why
 * the account menu was the one control in the product that read as a web page. The whole effect is
 * 6px of travel and a 3% scale over [Motion.StandardMs]; the work is done by `transform-origin`,
 * which ties the panel to its trigger so the eye never has to go looking for it.
 *
 * Compositor-only (`opacity`, `transform`), per the motion system's property rule, so this costs
 * nothing on a panel of any size. `prefers-reduced-motion` is neutralised globally in
 * `initLingvoTheme`, so there is no media query here.
 */
val LMenuStyle = CssStyle {
    base {
        Modifier
            .position(Position.Absolute)
            .top(100.percent)
            // The trigger's inline end, not its right. Pinning physically while the transform
            // origin below is logical would put the two in opposite corners in a right-to-left
            // interface, and the panel would grow away from the button that opened it.
            .styleModifier { property("inset-inline-end", "0") }
            .minWidth(220.px)
            .backgroundColor(colorMode.toSitePalette().surfaceRaised)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Card)
            // Depth, so the panel reads as being over the page rather than cut into it. Static:
            // the transition below names `opacity` and `transform` only, and a shadow that grew
            // with the entrance would animate a paint property on a surface that is already
            // moving, for no gain the eye can find.
            .elevation(Elevation.Floating)
            .padding(Space.XS)
            .zIndex(900)
            // The corner the panel is anchored by, named logically. [LMenuStyle] pins it to
            // `top: 100%; inset-inline-end: 0`, and an origin that disagreed with the anchor would
            // grow the menu away from its own trigger — which is exactly what a physical `top right`
            // does the moment the interface runs right-to-left. `--l-inline-end` is the theme
            // variable that exists for the properties with no logical spelling of their own.
            .styleModifier { property("transform-origin", "top var(--l-inline-end, right)") }
            // Fully visible at rest and while waiting for the first animation frame. A suspended
            // tab can delay motion, but it must never delay access to a focused surface.
            .styleModifier {
                property(
                    "transition",
                    "opacity ${Motion.StandardMs}ms ${Motion.EaseEntrance}, " +
                        "transform ${Motion.StandardMs}ms ${Motion.EaseEntrance}",
                )
            }
    }
    cssRule("[data-presence=\"entering\"]") {
        Modifier.styleModifier {
            property(
                "transform",
                "translateY(-${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})",
            )
        }
    }
    cssRule("[data-presence=\"exiting\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property(
                "transform",
                "translateY(-${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})",
            )
            property(
                "transition",
                "opacity ${Motion.FastMs}ms ${Motion.EaseIn}, " +
                    "transform ${Motion.FastMs}ms ${Motion.EaseIn}",
            )
            property("pointer-events", "none")
        }
    }
    // The three placements that are not the default. The base above *is* [LMenuPlacement.BOTTOM_END]
    // — every rule here overrides only what that placement got wrong for the corner in question, so
    // a menu that says nothing draws exactly the CSS it drew before this enum existed.
    //
    // `inset-inline-start`/`-end` rather than `left`/`right`, and `--l-inline-start`/`-end` for the
    // `transform-origin` that has no logical spelling, for the reason the base states: an origin
    // that disagrees with the anchor grows the panel away from its own trigger the moment the
    // interface runs right-to-left.
    cssRule("[data-menu-placement=\"bottom-start\"]") {
        Modifier.styleModifier {
            property("inset-inline-start", "0")
            property("inset-inline-end", "auto")
            property("transform-origin", "top var(--l-inline-start, left)")
        }
    }
    cssRule("[data-menu-placement=\"top-start\"]") {
        Modifier.styleModifier {
            property("top", "auto")
            property("bottom", "100%")
            property("inset-inline-start", "0")
            property("inset-inline-end", "auto")
            property("transform-origin", "bottom var(--l-inline-start, left)")
        }
    }
    cssRule("[data-menu-placement=\"top-end\"]") {
        Modifier.styleModifier {
            property("top", "auto")
            property("bottom", "100%")
            property("transform-origin", "bottom var(--l-inline-end, right)")
        }
    }
    // **A panel above its trigger has to travel the other way.** The 6px of entrance movement is
    // "from where the trigger is towards where the panel will rest"; upwards is correct only while
    // the panel is below the trigger. Above it, the same `-6px` starts the panel *further from* the
    // control that opened it and slides it away, which is the one thing the origin work above
    // exists to prevent. Only the transform is restated — the duration, the easing and the
    // `pointer-events` of the exit come from the two lower-specificity rules and are unchanged.
    cssRule("[data-menu-placement^=\"top\"][data-presence=\"entering\"]") {
        Modifier.styleModifier {
            property(
                "transform",
                "translateY(${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})",
            )
        }
    }
    cssRule("[data-menu-placement^=\"top\"][data-presence=\"exiting\"]") {
        Modifier.styleModifier {
            property(
                "transform",
                "translateY(${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})",
            )
        }
    }
}

val LMenuItemStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            // Missed the hit-area floor entirely — every other pressable control in the design
            // system states it, this one didn't. `minHeight`, not `height`: a menu item's own
            // padding already clears 40 at one line, and a wrapped or icon-bearing label has to be
            // allowed to grow past it rather than being clipped back down to it.
            .minHeight(HIT_AREA_FLOOR_PX.px)
            .backgroundColor(Color.rgba(0, 0, 0, 0f))
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, Color.rgba(0, 0, 0, 0f))
            .borderRadius(Radius.Control)
            .padding(topBottom = Space.SM, leftRight = Space.MD)
            .fontSize(TypeScale.BodySmall)
            .fontWeight(500)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "8px")
                // A `<button>`'s user-agent default is `text-align: center`, inherited down into
                // every span this row nests — the label and its description both. Nobody overrode
                // it, so a label long enough to wrap ("Fill from translation memory only" at
                // 220px) wrapped into two *centred* lines with the description centred under it,
                // both reading as ragged text rather than a left-aligned list a reader can scan.
                property("text-align", "start")
            }
            .transition {
                property("background-color", "color")
                duration(Motion.FastMs.ms)
                timingFunction(Motion.EaseOutTiming)
            }
    }
    cssRule(":not(:disabled):hover") {
        Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
    // `[data-disabled="true"]`, not the native `:disabled` pseudo-class this used to be — the real
    // `disabled()` attribute stays (below, for focus and click behaviour), but the paint now comes
    // from the one shared `disabledSurface()` every control in the design system uses.
    cssRule("[data-disabled=\"true\"]") { Modifier.disabledSurface() }
}

/**
 * The label column, so a description can sit under the label instead of beside it.
 *
 * `min-width: 0` is what keeps a long description from pushing the shortcut off the panel: a flex
 * item's default minimum is its content, so without this the row grows to fit the longest sentence
 * in it and the `<kbd>` at the inline end leaves the border behind.
 */
val LMenuItemLabelStyle = CssStyle.base {
    Modifier
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("flex", "1 1 auto")
            property("min-width", "0")
            property("align-items", "flex-start")
            property("gap", "2px")
        }
}

/** The consequence, under the command that has it. Never the only place a consequence is stated. */
val LMenuItemDescriptionStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Micro)
        .fontWeight(400)
}

/**
 * The shortcut, pinned to the inline end.
 *
 * `margin-inline-start: auto` rather than `justify-content: space-between` on the row: the row also
 * holds an icon and a checkmark, and pushing them apart would spread those away from the label too.
 */
val LMenuItemShortcutStyle = CssStyle.base {
    Modifier.styleModifier {
        property("margin-inline-start", "auto")
        property("flex", "0 0 auto")
    }
}

/**
 * The checkmark column of a `menuitemcheckbox` row.
 *
 * Reserved whether or not the item is checked, so the labels of a group of settings line up with
 * each other instead of shifting sideways as they are toggled. Present only on rows that are
 * settings — a menu of plain commands draws no indicator column at all, because an empty one reads
 * as a group of checkboxes nobody has ticked.
 */
val LMenuItemCheckStyle = CssStyle.base {
    Modifier
        .iconSize(IconSize.MD)
        .styleModifier {
            property("display", "inline-flex")
            property("flex", "0 0 auto")
            property("align-items", "center")
            property("justify-content", "center")
        }
}

/** The name of a run of items. Presentation only — see [LMenuGroup]. */
val LMenuGroupTitleStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Micro)
        .fontWeight(600)
        .padding(topBottom = Space.XS, leftRight = Space.MD)
        .styleModifier { property("letter-spacing", "0.04em") }
}

/**
 * The rule between two runs of items.
 *
 * `flex: 0 0 auto` because the panel is a flex column: a 1px child is the first thing a column with
 * anything to shrink will shrink, and a separator that is sometimes zero pixels tall is a grouping
 * that appears and disappears with the length of the menu.
 */
val LMenuSeparatorStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().border)
        .width(100.percent)
        .height(1.px)
        .margin(topBottom = Space.XS)
        .styleModifier { property("flex", "0 0 auto") }
}

/**
 * The overload every menu in the product was written against: a flat list of commands.
 *
 * Kept, rather than migrated in one sweep, because the two lists mean the same thing — a
 * `List<LMenuItem>` *is* a list of entries — and rewriting six call sites to say so would have put
 * a mechanical diff in front of every reviewer of this change while adding nothing a reader of
 * those files needs. New menus that want a separator, a group, a shortcut or a checkmark reach for
 * the [LMenuEntry] overload below; the ones that want six commands in a column keep this.
 */
@Composable
fun LMenu(
    expanded: Boolean,
    items: List<LMenuItem>,
    onExpandedChange: (Boolean) -> Unit,
    onSelect: (Int) -> Unit,
    modifier: Modifier = Modifier,
    focusPolicy: LMenuFocusPolicy = LMenuFocusPolicy.OWNED,
    trigger: @Composable (onToggle: () -> Unit, triggerModifier: Modifier) -> Unit,
) {
    LMenu(
        expanded = expanded,
        entries = items,
        onExpandedChange = onExpandedChange,
        onSelect = onSelect,
        modifier = modifier,
        placement = LMenuPlacement.BOTTOM_END,
        focusPolicy = focusPolicy,
        trigger = trigger,
    )
}

/**
 * A menu of [LMenuEntry] rows.
 *
 * **[onSelect] is given the index into [entries], not into the commands within it.** A menu that
 * gains a separator would otherwise renumber every command after it, and the renumbering is silent:
 * the code still compiles, the menu still opens, and one row starts doing the next row's job. The
 * index a caller receives is always the position of the entry they wrote, so `entries[index]` is
 * the thing that was pressed.
 */
@Composable
fun LMenu(
    expanded: Boolean,
    entries: List<LMenuEntry>,
    onExpandedChange: (Boolean) -> Unit,
    onSelect: (Int) -> Unit,
    modifier: Modifier = Modifier,
    placement: LMenuPlacement = LMenuPlacement.BOTTOM_END,
    focusPolicy: LMenuFocusPolicy = LMenuFocusPolicy.OWNED,
    trigger: @Composable (onToggle: () -> Unit, triggerModifier: Modifier) -> Unit,
) {
    val menuId = remember { "lingvo-menu-${Random.nextInt(1_000_000)}" }
    val firstEnabledIndex = entries.indexOfFirst { it is LMenuItem && !it.disabled }
    // Whether the panel reserves a checkmark column. A menu of plain commands must not: an empty
    // indicator column in front of every row reads as a set of checkboxes that are all off.
    val hasCheckableItem = entries.any { it is LMenuItem && it.checked != null }
    // Whether the panel reserves an icon column, the way `PickerRow` reserves its check column.
    // A mix of iconed and bare commands ("Duplicate" beside "Save now", "Keyboard shortcuts") drew
    // every bare label flush against the row's edge while an iconed row's label sat a icon-width
    // further in — no shared left edge for a reader's eye to land on. Reserved for every row once
    // any row in the panel carries one, so the icons and the bare labels line up on the same edge
    // rather than each row solving its own left margin.
    val hasIconItem = entries.any { it is LMenuItem && it.icon != null }
    var instantMotion by remember { mutableStateOf(false) }
    val presence = rememberLPresence(expanded, Motion.FastMs, settleImmediately = instantMotion)
    // A description or a shortcut chip needs room `LMenuStyle`'s 220px floor does not give it: at
    // that width "Fill from translation memory only" wrapped across two centred lines, and a
    // shortcut chip crowded a label like "Next untranslated" into wrapping against it. Widened
    // only for menus that actually carry either, so a plain command list keeps its narrower panel.
    // A description gets the wider of the two floors: 280px still wrapped "Fill from translation
    // memory only" onto a second line once the reserved icon and check columns ate into it.
    val hasDescription = entries.any { it is LMenuItem && it.description != null }
    val hasShortcut = entries.any { it is LMenuItem && it.shortcut != null }
    val minPanelWidthPx = when {
        hasDescription -> 320
        hasShortcut -> 280
        else -> 240
    }

    // **Only give focus back to a menu that was actually opened.**
    //
    // "Closed and not rendered" is also the state every menu is in on its first composition, so
    // without this flag the restore branch fires the moment a page containing a menu mounts and
    // drags focus onto that menu's trigger — away from the heading a reader just landed on, or out
    // of the field they were typing in when a row-level menu appeared. Restoring focus is only
    // correct as the second half of an interaction this component itself started.
    var everExpanded by remember { mutableStateOf(false) }
    LaunchedEffect(expanded, presence.rendered, presence.phase, firstEnabledIndex, focusPolicy) {
        val active = document.activeElement
        if (expanded && presence.rendered && firstEnabledIndex >= 0) {
            everExpanded = true
            // **Take focus on the way in, and never again while it is already inside.**
            //
            // `firstEnabledIndex` is one of this effect's keys, so the effect restarts whenever an
            // item's `disabled` changes — which happens *while the menu is open*, from a background
            // save or a permission arriving. Focusing unconditionally then yanked the reader from
            // the item they had arrowed to back to the first enabled one, and if the index moved
            // twice (a save starting, then finishing) it yanked twice. Press Enter on the second
            // one and the menu fires an action the reader never navigated to.
            val panel = document.getElementById(menuId)
            if (panel == null || active == null || !panel.contains(active)) {
                (document.getElementById("$menuId-item-$firstEnabledIndex") as? HTMLElement)?.focus()
            }
        } else if (everExpanded && !expanded && !presence.rendered) {
            // Restore only what was actually lost. Closing by Escape, by selecting an item or by
            // clicking the scrim leaves focus on `<body>`, because the element that held it has
            // just been removed — that is the case this exists for. Closing by Tab does not: focus
            // is already on the control the reader asked to move to, and pulling it back to the
            // trigger would undo the very keystroke that closed the menu.
            val strandedOnBody = active == null || active == document.body
            if (focusPolicy == LMenuFocusPolicy.OWNED && strandedOnBody) {
                (document.querySelector("[aria-controls='$menuId']") as? HTMLElement)?.focus()
            }
        }
    }

    Box(LMenuRootStyle.toModifier().then(modifier)) {
        trigger(
            { onExpandedChange(!expanded) },
            Modifier
                .attr("aria-haspopup", "menu")
                .attr("aria-expanded", expanded.toString())
                .attr("aria-controls", menuId)
                .onClick { event -> instantMotion = (event.nativeEvent as MouseEvent).detail == 0 },
        )
        if (presence.rendered) {
            val exiting = presence.phase == LPresencePhase.EXITING
            Div(LMenuScrimStyle.toModifier().toAttrs {
                attr("aria-hidden", "true")
                attr("data-presence", presence.phase.attributeValue)
                if (exiting) attr("inert", "")
                onClick {
                    instantMotion = false
                    onExpandedChange(false)
                }
            })
            Column(
                LMenuStyle.toModifier()
                    .styleModifier { property("min-inline-size", "${minPanelWidthPx}px") }
                    // The gap between trigger and panel goes on whichever side the panel is not
                    // anchored by. `margin(top = …)` on a panel opening upwards pushes it *into*
                    // its own trigger, which is how far this can go wrong for one word.
                    .then(
                        if (placement.opensUpwards) {
                            Modifier.margin(bottom = Space.SM)
                        } else {
                            Modifier.margin(top = Space.SM)
                        },
                    )
                    .toAttrsModifier(menuId)
                    .attr("data-menu-placement", placement.attributeValue)
                    .attr("data-presence", presence.phase.attributeValue)
                    .then(if (instantMotion) Modifier.noTransition() else Modifier)
                    .then(if (exiting) Modifier.attr("inert", "").attr("aria-hidden", "true") else Modifier),
            ) {
                entries.forEachIndexed { index, entry ->
                    when (entry) {
                        is LMenuSeparator -> Div(
                            LMenuSeparatorStyle.toModifier().toAttrs { attr("role", "separator") },
                        )

                        is LMenuGroup -> Div(
                            LMenuGroupTitleStyle.toModifier().toAttrs { attr("role", "presentation") },
                        ) {
                            Text(entry.title)
                        }

                        is LMenuItem -> LMenuItemRow(
                            item = entry,
                            menuId = menuId,
                            index = index,
                            tabbable = index == firstEnabledIndex,
                            reserveCheckColumn = hasCheckableItem,
                            reserveIconColumn = hasIconItem,
                            onSelect = onSelect,
                            onInputMotion = { instantMotion = it },
                            onClose = { onExpandedChange(false) },
                        )
                    }
                }
            }
        }
    }
}

/** True for the two placements that put the panel above its trigger. */
private val LMenuPlacement.opensUpwards: Boolean
    get() = this == LMenuPlacement.TOP_START || this == LMenuPlacement.TOP_END

/**
 * Whether a label is left free to wrap.
 *
 * Most commands are a verb and a short object — "Duplicate", "Next untranslated" — and those must
 * never wrap against a shortcut chip sitting at the row's inline end (`EditorActionBar`'s overflow
 * menu measured exactly that: "Next untranslated" broke into two lines fighting its own `<kbd>`).
 * A genuinely long label — "Fill from translation memory only" — is left to wrap rather than
 * overflowing the panel or being cut with an ellipsis a reader cannot expand. 28 characters is
 * roughly where a label stops reading as a short verb phrase and starts reading as a sentence.
 */
private fun shouldWrapLabel(label: String): Boolean = label.length > 28

/**
 * One command row: indicator, icon, label over description, shortcut.
 *
 * A separate composable because the row grew four optional parts and the `forEachIndexed` above was
 * becoming the only place in the file where the *structure* of the menu was hard to see.
 */
@Composable
private fun LMenuItemRow(
    item: LMenuItem,
    menuId: String,
    index: Int,
    tabbable: Boolean,
    reserveCheckColumn: Boolean,
    reserveIconColumn: Boolean,
    onSelect: (Int) -> Unit,
    onClose: () -> Unit,
    onInputMotion: (Boolean) -> Unit,
) {
    val shortcutKeys = remember(item.shortcut) { item.shortcut?.let(::chordKeys).orEmpty() }
    Button(
        LMenuItemStyle.toModifier()
            .then(
                if (item.danger) {
                    Modifier.color(ColorMode.current.toSitePalette().danger)
                } else {
                    Modifier
                },
            )
            .toAttrs {
                id("$menuId-item-$index")
                type(ButtonType.Button)
                // A row that toggles something is not a row that does something, and the two are
                // told apart by role rather than by the tick a listener cannot see.
                attr("role", if (item.checked != null) "menuitemcheckbox" else "menuitem")
                item.checked?.let { attr("aria-checked", it.toString()) }
                tabIndex(if (tabbable) 0 else -1)
                attr("data-disabled", item.disabled.toString())
                if (item.disabled) disabled()
                onClick { event ->
                    if (!item.disabled) {
                        onInputMotion((event.nativeEvent as MouseEvent).detail == 0)
                        onSelect(index)
                        onClose()
                    }
                }
                onKeyDown { event ->
                    if (event.key == "Escape" || event.key == "Tab") onInputMotion(true)
                    handleMenuKey(event, onClose = onClose)
                }
            },
    ) {
        if (reserveCheckColumn) {
            Span(LMenuItemCheckStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
                if (item.checked == true) LCheckIcon(Modifier.iconSize(IconSize.MD))
            }
        }
        // Reserved whether or not *this* item has an icon, once any row in the panel does — the
        // same reasoning as `reserveCheckColumn` above. A bare "Save now" beside an iconed
        // "Duplicate" used to start its label where the icon column left off on one row and flush
        // against the edge on the other, so the panel had no shared left edge for either column.
        if (reserveIconColumn) {
            // The row's flex is `align-items: center`, which is right for a one-line item but pins
            // a two-line one's icon to its vertical midpoint — between the label and the
            // description rather than beside either. `flex-start` plus a small top nudge lines the
            // icon up with the label's own line instead.
            val descriptionAligned = item.icon != null && item.description != null
            Span(
                LMenuItemCheckStyle.toModifier()
                    .then(
                        if (descriptionAligned) {
                            Modifier.styleModifier {
                                property("align-self", "flex-start")
                                property("margin-top", "2px")
                            }
                        } else {
                            Modifier
                        },
                    )
                    .toAttrs { attr("aria-hidden", "true") },
            ) {
                item.icon?.invoke()
            }
        }
        Span(LMenuItemLabelStyle.toModifier().toAttrs()) {
            Span(
                Modifier.thenIf(!shouldWrapLabel(item.label)) {
                    Modifier.styleModifier { property("white-space", "nowrap") }
                }.toAttrs(),
            ) { Text(item.label) }
            item.description?.let { description ->
                Span(LMenuItemDescriptionStyle.toModifier().toAttrs()) { Text(description) }
            }
        }
        if (shortcutKeys.isNotEmpty()) {
            LChord(shortcutKeys, LMenuItemShortcutStyle.toModifier())
        }
    }
}

private fun Modifier.toAttrsModifier(menuId: String): Modifier =
    this
        .attr("id", menuId)
        .attr("role", "menu")
        .attr("aria-label", tr("ds.ds.lmenu.actions", "Actions"))

private fun handleMenuKey(
    event: SyntheticKeyboardEvent,
    onClose: () -> Unit,
) {
    val current = event.target as? HTMLElement ?: return
    val parent = current.parentElement ?: return
    // Both roles, because a settings row is a `menuitemcheckbox` and arrowing past one would
    // otherwise skip it — silently, since the row is still visible and still focusable by hand.
    val nodes = parent.querySelectorAll(
        "[role='menuitem']:not(:disabled), [role='menuitemcheckbox']:not(:disabled)",
    )
    val enabledItems = buildList {
        for (index in 0 until nodes.length) {
            (nodes.item(index) as? HTMLElement)?.let(::add)
        }
    }
    if (enabledItems.isEmpty()) return
    val currentIndex = enabledItems.indexOf(current).coerceAtLeast(0)

    when (event.key) {
        "ArrowDown" -> {
            event.preventDefault()
            enabledItems[(currentIndex + 1) % enabledItems.size].focus()
        }
        "ArrowUp" -> {
            event.preventDefault()
            enabledItems[(currentIndex - 1 + enabledItems.size) % enabledItems.size].focus()
        }
        "Home" -> {
            event.preventDefault()
            enabledItems.first().focus()
        }
        "End" -> {
            event.preventDefault()
            enabledItems.last().focus()
        }
        // Tab is the reader leaving, not navigating, and it is deliberately not prevented: the
        // menu closes and the browser's own move goes ahead. Without this the panel stayed open
        // behind them with its full-viewport scrim still mounted, so the next control they tabbed
        // to was visible, focusable, and swallowed every click.
        "Tab" -> onClose()
        "Escape" -> {
            event.preventDefault()
            onClose()
        }
    }
}
